import { createFileRoute, Link, useNavigate, Navigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDefaultAgentType, getInitialAgentType, type AgentType } from "@/hooks/use-default-agent";
import { THREAD_AGENT_OPTIONS } from "@/lib/agent-options";
import {
  Plus,
  Archive,
  ArchiveRestore,
  ChevronRight,
  ChevronDown,
  GitBranch,
  MessageSquare,
  AlertTriangle,
  Settings2,
  Rocket,
  Search,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth";
import {
  fetchSessionList,
  fetchCompleteSessionList,
  getSessionListQueryKey,
} from "@/lib/session-list-query";
import { normalizeArrayResponse } from "@/lib/repository-route-response-normalizers";
import { canShowRepositoryDeployUi } from "@/lib/repository-deploy-ui";
import { cn, relativeTime, langColors, formatSessionTitle } from "@/lib/utils";
import { Tooltip } from "@/components/ui/Tooltip";
import { ThreadStatusBadge } from "@/components/ui/ThreadStatusBadge";
import { fetchGitStatus, type GitStatus } from "@/hooks/use-git";
import { useMobileLayout } from "@/hooks/use-mobile-layout";

import { cleanupSessionLocalStorage } from "@/lib/session-localstorage-cleanup";
import { clearPersistableSessionSurfaceQueriesForSession } from "@/lib/persisted-session-surface-query-cache";
import { useSessionPrFlow } from "@/stores/session-pr-flow";
import {
  buildOptimisticEmptyThreadSessionRecord,
  mergeSessionRecordPreservingHasHistory,
} from "@/lib/optimistic-empty-thread-session-record";
import {
  useThreadRuntime,
} from "@/stores/thread-runtime";
import { deriveSidebarThreadStatusState } from "@/lib/sidebar-thread-status-state-machine";
import { useThreadRuntimeSessionRowMirror } from "@/hooks/use-thread-runtime-session-row-mirror";
import { useHasProvider } from "@/hooks/use-has-provider";
import {
  ANALYTICS_EVENTS,
  buildPrAnalyticsProperties,
  buildThreadAnalyticsProperties,
  captureAnalyticsEvent,
} from "@/lib/analytics";
import {
  hasSessionChangesForArchiveConfirmation,
  isRunningSandboxForArchiveConfirmation,
  RUNNING_SANDBOX_ARCHIVE_CONFIRMATION_MESSAGE,
} from "@/lib/running-sandbox-confirmation-messages";
import {
  getRepoFileTreeQueryOptions,
} from "@/hooks/use-repo-file-search";
import { warmNewSessionFileTreeFromRepoSnapshot } from "@/lib/new-session-file-tree-hydration";
import { RepoIcon } from "@/components/layout/RepoIcon";
import {
  applyOptimisticSessionMembershipToSessionListCaches,
  captureSessionCacheMutationSnapshot,
  restoreSessionCacheMutationSnapshot,
  selectSessionCacheMutationSource,
  type SessionCacheMutationSnapshot,
} from "@/lib/session-membership-cache";

export const Route = createFileRoute("/_authed/$repoId/")({
  component: RepoDetailPage,
});

/**
 * When a thread page seeds repo list caches optimistically before navigating
 * back here, we want this screen to trust that cache briefly instead of
 * immediately refetching stale pre-mutation data from the server. Five seconds
 * comfortably covers normal archive/delete round trips while still letting the
 * explicit invalidations on settle re-sync us quickly.
 */
const SESSION_LIST_OPTIMISTIC_STALE_TIME_MS = 5_000;

/**
 * Number of archived threads above which the search input becomes visible.
 * Below this count a user can scan the list visually without needing search.
 * Kept low enough to be useful for moderate-activity repos.
 */
const ARCHIVED_SEARCH_VISIBILITY_THRESHOLD = 10;

function ownedRepoSessionsQueryKey(args: {
  repositoryId: string;
  viewerUserId: string | null;
  archived: boolean;
}) {
  return [
    "sessions",
    "owned-repository-list",
    args.repositoryId,
    args.viewerUserId,
    args.archived ? "archived" : "active",
  ] as const;
}

interface AgentSession {
  id: string;
  repositoryId?: string;
  userId?: string;
  title: string | null;
  status: string;
  agentType: string;
  createdAt: string;
  updatedAt?: string;
  messageCount: number | null;
  lifecycleStatus: string;
  prState?: string | null;
  archivedAt?: string | null;
  hasHistory?: boolean;
  organizationThread?: boolean;
  owner?: unknown;
}

function filterOwnedRepoSessions(args: {
  sessions: AgentSession[];
  repositoryId: string;
  viewerUserId: string | null;
}): AgentSession[] {
  return args.sessions.filter((session) => {
    if (session.repositoryId !== args.repositoryId) return false;
    if (session.organizationThread === true || session.owner !== undefined) return false;
    if (
      args.viewerUserId !== null &&
      typeof session.userId === "string" &&
      session.userId !== args.viewerUserId
    ) {
      return false;
    }
    return true;
  });
}



function isDeleting(s: AgentSession) {
  return s.lifecycleStatus === "deleting";
}

function isDeleteFailed(s: AgentSession) {
  return s.lifecycleStatus === "delete_failed";
}

function restoreSessionInList(
  old: AgentSession[] | undefined,
  restored: AgentSession,
): AgentSession[] {
  const next = [...(old ?? [])];
  if (next.some((s) => s.id === restored.id)) return next;
  next.push(restored);
  next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return next;
}

function upsertSessionInList(
  old: AgentSession[] | undefined,
  item: AgentSession,
  sortField: "createdAt" | "archivedAt" = "createdAt",
): AgentSession[] {
  const next = [...(old ?? [])];
  const idx = next.findIndex((s) => s.id === item.id);
  if (idx >= 0) {
    next[idx] = item;
  } else {
    next.push(item);
  }
  next.sort((a, b) => {
    const aValue = sortField === "archivedAt" ? (a.archivedAt ?? a.createdAt) : a.createdAt;
    const bValue = sortField === "archivedAt" ? (b.archivedAt ?? b.createdAt) : b.createdAt;
    return new Date(bValue).getTime() - new Date(aValue).getTime();
  });
  return next;
}

function RepoDetailPage() {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: authSession } = authClient.useSession();
  const viewerUserId = authSession?.user?.id ?? null;
  const activeSessionsQueryKey = ownedRepoSessionsQueryKey({
    repositoryId: repoId,
    viewerUserId,
    archived: false,
  });
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType>(getInitialAgentType);
  const stableSetAgent = useCallback((v: AgentType) => setSelectedAgentType(v), []);
  useDefaultAgentType(stableSetAgent, repoId);

  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [archivedSearchQuery, setArchivedSearchQuery] = useState("");
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [archiveMutatingIds, setArchiveMutatingIds] = useState<Set<string>>(new Set());
  const agentDropdownRef = useRef<HTMLDivElement>(null);
  const isMobileLayout = useMobileLayout();
  const { data: hasProviderData } = useHasProvider();
  const hasProvider = hasProviderData?.hasProvider !== false;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(e.target as Node)) {
        setAgentDropdownOpen(false);
      }
    }
    if (agentDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [agentDropdownOpen]);


  const { data: repos } = useQuery({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<{
        id: string;
        name: string;
        provider?: "github" | "gitea" | string;
        githubFullName: string;
        displayName?: string | null;
        repositoryFullName?: string | null;
        language: string | null;
        defaultBranch: string;
        iconUpdatedAt?: string | null;
        deployEnabled?: boolean;
        isCordNewProject?: boolean;
      }>({ value: await res.json() });
    },
  });

  const repo = (repos ?? []).find((r) => r.id === repoId);

  useEffect(() => {
    if (!repo) return;

    void queryClient.prefetchQuery(
      getRepoFileTreeQueryOptions(repo.id, repo.defaultBranch),
    );
  }, [queryClient, repo?.defaultBranch, repo?.id]);

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: activeSessionsQueryKey,
    staleTime: SESSION_LIST_OPTIMISTIC_STALE_TIME_MS,
    queryFn: async () => {
      return filterOwnedRepoSessions({
        sessions: await fetchSessionList<AgentSession>({ repositoryId: repoId }),
        repositoryId: repoId,
        viewerUserId,
      });
    },
  });

  // Use the same query key and fetcher as the sidebar so we share the complete
  // archived sessions cache. This avoids the 200-item cap of fetchSessionList
  // and prevents a redundant network request when the sidebar has already loaded.
  const allArchivedQueryKey = getSessionListQueryKey({ archived: true, includeRuntimeConversations: false });
  const { data: allArchivedSessions, isLoading: archivedLoading } = useQuery({
    queryKey: allArchivedQueryKey,
    staleTime: SESSION_LIST_OPTIMISTIC_STALE_TIME_MS,
    queryFn: () => fetchCompleteSessionList<AgentSession>({ archived: true, includeRuntimeConversations: false }),
  });

  // Derive repo-specific archived list from the complete list on the client side.
  const archivedSessions = useMemo(
    () =>
      allArchivedSessions
        ? filterOwnedRepoSessions({
            sessions: allArchivedSessions,
            repositoryId: repoId,
            viewerUserId,
          })
        : undefined,
    [allArchivedSessions, repoId, viewerUserId],
  );

  const deleteSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await api.api.sessions[":id"].$delete({
        param: { id: sessionId },
      });
      if (res.status === 404) return;
      if (!res.ok) {
        const err = new Error("Failed to delete session");
        (err as Error & { status?: number }).status = res.status;
        throw err;
      }
    },
    onMutate: async (sessionId: string) => {
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      // Track which session is being deleted for per-row disable
      setDeletingIds((prev) => new Set(prev).add(sessionId));
      // Snapshot for rollback
      const prevRepo = queryClient.getQueryData<AgentSession[]>(activeSessionsQueryKey);
      const prevAll = queryClient.getQueryData<AgentSession[]>(["sessions"]);
      const removedRepo = prevRepo?.find((s) => s.id === sessionId);
      const removedAll = prevAll?.find((s) => s.id === sessionId);

      // Optimistically remove the session from all caches immediately
      queryClient.setQueryData(activeSessionsQueryKey, (old: AgentSession[] | undefined) =>
        old?.filter((s) => s.id !== sessionId)
      );
      queryClient.setQueryData(["sessions"], (old: AgentSession[] | undefined) =>
        old?.filter((s) => s.id !== sessionId)
      );

      return { removedRepo, removedAll };
    },
    onSuccess: (_data, sessionId) => {
      setDeletingIds((prev) => { const next = new Set(prev); next.delete(sessionId); return next; });
      clearPersistableSessionSurfaceQueriesForSession({ queryClient, sessionId })
      cleanupSessionLocalStorage(sessionId);
      queryClient.invalidateQueries({ queryKey: activeSessionsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", "archived"] });
    },
    onError: (_err, sessionId, context) => {
      setDeletingIds((prev) => { const next = new Set(prev); next.delete(sessionId); return next; });
      // Rollback: restore the caches to pre-delete state
      const removedRepo = context?.removedRepo;
      if (removedRepo) {
        queryClient.setQueryData(activeSessionsQueryKey, (old: AgentSession[] | undefined) =>
          restoreSessionInList(old, removedRepo)
        );
      }
      const removedAll = context?.removedAll;
      if (removedAll) {
        queryClient.setQueryData(["sessions"], (old: AgentSession[] | undefined) =>
          restoreSessionInList(old, removedAll)
        );
      }
    },
  });

  const archiveSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/archive`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to archive session");
      }
      return res.json() as Promise<{ session?: AgentSession }>;
    },
    onMutate: async (sessionId: string) => {
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      await queryClient.cancelQueries({ queryKey: ["session", sessionId] });
      setArchiveMutatingIds((prev) => new Set(prev).add(sessionId));
      const nowIso = new Date().toISOString();

      const snapshot = captureSessionCacheMutationSnapshot({ queryClient, sessionId, repoId });
      const sourceItem = selectSessionCacheMutationSource({ snapshot, sessionId });
      const archivedSession = sourceItem ? { ...sourceItem, archivedAt: nowIso } : null;

      queryClient.setQueryData(["session", sessionId], (old: unknown) => {
        if (!old || typeof old !== "object") return archivedSession ?? old;
        return { ...(old as Record<string, unknown>), archivedAt: nowIso };
      });
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot,
        sessionId,
        item: archivedSession,
      });

      return { snapshot };
    },
    onError: (_err, _sessionId, context) => {
      if (!context?.snapshot) return;
      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: _sessionId,
        snapshot: context.snapshot,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activeSessionsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", "archived"] });
    },
    onSettled: (_data, _error, sessionId) => {
      setArchiveMutatingIds((prev) => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    },
  });

  const getArchiveConfirmationHasSessionChanges = useCallback((sessionId: string): boolean | null => {
    const cachedGitStatus = queryClient.getQueryData<GitStatus>(["git-status", sessionId]);
    if (cachedGitStatus) {
      return hasSessionChangesForArchiveConfirmation({
        stagedCount: cachedGitStatus.staged.length,
        unstagedCount: cachedGitStatus.unstaged.length,
        untrackedCount: cachedGitStatus.untracked.length,
        commitsAheadOfBase: cachedGitStatus.commitsAheadOfBase,
      });
    }

    const cachedPrFlowSnapshot = useSessionPrFlow.getState().snapshots[sessionId] ?? null;
    if (cachedPrFlowSnapshot?.repoReadiness) {
      return cachedPrFlowSnapshot.repoReadiness.hasQuickPrSourceChanges;
    }

    return null;
  }, [queryClient]);

  const resolveArchiveConfirmationHasSessionChanges = useCallback(async (
    sessionId: string,
    prState?: string | null,
  ): Promise<boolean | null> => {
    const cachedHasSessionChanges = getArchiveConfirmationHasSessionChanges(sessionId);
    if (typeof cachedHasSessionChanges === "boolean") {
      return cachedHasSessionChanges;
    }

    if (prState !== "merged") {
      return null;
    }

    try {
      const freshGitStatus = await fetchGitStatus(sessionId);
      queryClient.setQueryData(["git-status", sessionId], freshGitStatus);

      return hasSessionChangesForArchiveConfirmation({
        stagedCount: freshGitStatus.staged.length,
        unstagedCount: freshGitStatus.unstaged.length,
        untrackedCount: freshGitStatus.untracked.length,
        commitsAheadOfBase: freshGitStatus.commitsAheadOfBase,
      });
    } catch {
      return null;
    }
  }, [getArchiveConfirmationHasSessionChanges, queryClient]);

  const unarchiveSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/unarchive`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to unarchive session");
      }
      return res.json() as Promise<{ session?: AgentSession }>;
    },
    onMutate: async (sessionId: string) => {
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      await queryClient.cancelQueries({ queryKey: ["session", sessionId] });
      setArchiveMutatingIds((prev) => new Set(prev).add(sessionId));

      const snapshot = captureSessionCacheMutationSnapshot({ queryClient, sessionId, repoId });
      const sourceItem = selectSessionCacheMutationSource({ snapshot, sessionId });
      const activeSession = sourceItem ? { ...sourceItem, archivedAt: null } : null;

      queryClient.setQueryData(["session", sessionId], (old: unknown) => {
        if (!old || typeof old !== "object") return activeSession ?? old;
        return { ...(old as Record<string, unknown>), archivedAt: null };
      });
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot,
        sessionId,
        item: activeSession,
      });

      return { snapshot };
    },
    onError: (_err, _sessionId, context) => {
      if (!context?.snapshot) return;
      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: _sessionId,
        snapshot: context.snapshot,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activeSessionsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", "archived"] });
    },
    onSettled: (_data, _error, sessionId) => {
      setArchiveMutatingIds((prev) => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    },
  });

  const createSessionPending = useRef(false);

  const createSession = useMutation({
    mutationFn: async ({ agentType, sessionId, title }: { agentType: AgentType; sessionId: string; title: string; cacheSnapshot: SessionCacheMutationSnapshot }) => {
      const res = await api.api.sessions.$post({
        json: { repositoryId: repoId, title, agentType, id: sessionId },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error ?? "Failed to create session");
      }
      return res.json() as unknown as AgentSession;
    },
    onSuccess: (data, vars) => {
      const existingSession = queryClient.getQueryData<AgentSession>(["session", data.id]);
      const mergedSession = mergeSessionRecordPreservingHasHistory({
        incoming: data as AgentSession & Record<string, unknown>,
        existing: existingSession as (AgentSession & Record<string, unknown>) | undefined,
      }) as AgentSession;
      queryClient.setQueryData<AgentSession | undefined>(["session", data.id], mergedSession);
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot: vars.cacheSnapshot,
        sessionId: data.id,
        item: mergedSession as AgentSession & Record<string, unknown>,
      });
      queryClient.invalidateQueries({ queryKey: activeSessionsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", "archived"] });
    },
    onError: (err, vars) => {
      captureAnalyticsEvent({
        event: ANALYTICS_EVENTS.threadCreateFailed,
        properties: buildThreadAnalyticsProperties({
          sessionId: vars.sessionId,
          repositoryId: repoId,
          agentType: vars.agentType,
          source: "repo_detail",
          properties: {
            error_message: err instanceof Error ? err.message : "Failed to create session",
          },
        }),
      });
      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: vars.sessionId,
        snapshot: vars.cacheSnapshot,
      });
      sessionStorage.removeItem(`cord:session-title:${vars.sessionId}`);
      sessionStorage.removeItem(`cord:session-agent-type:${vars.sessionId}`);
      if (err instanceof Error && err.message === "NO_PROVIDER_CONFIGURED") {
        navigate({ to: "/settings", search: { tab: "api-keys" as any } });
      } else {
        navigate({ to: "/$repoId", params: { repoId } });
      }
    },
    onSettled: () => {
      createSessionPending.current = false;
    },
  });

  const handleNewThread = useCallback((agentType: AgentType) => {
    if (createSessionPending.current || !hasProvider) return;
    createSessionPending.current = true;

    const sessionId = crypto.randomUUID();
    const title = formatSessionTitle();

    // Optimistically add the session to caches so sidebar updates immediately
    const placeholder = {
      ...buildOptimisticEmptyThreadSessionRecord({
        sessionId,
        repositoryId: repoId,
        title,
        agentType,
      }),
      messageCount: null,
      archivedAt: null,
    } satisfies AgentSession & { hasHistory: false };
    void queryClient.cancelQueries({ queryKey: ["sessions"] });
    const cacheSnapshot = captureSessionCacheMutationSnapshot({
      queryClient,
      sessionId,
      repoId,
    });
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient,
      snapshot: cacheSnapshot,
      sessionId,
      item: placeholder,
    });
    queryClient.setQueryData(["session", sessionId], placeholder);

    // Stash optimistic title so the session page can show it instantly
    sessionStorage.setItem(`cord:session-title:${sessionId}`, title);
    sessionStorage.setItem(`cord:session-agent-type:${sessionId}`, agentType);

    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.threadCreated,
      properties: buildThreadAnalyticsProperties({
        sessionId,
        repositoryId: repoId,
        agentType,
        source: "repo_detail",
      }),
    });

    warmNewSessionFileTreeFromRepoSnapshot({
      queryClient,
      repoId,
      sessionId,
      branch: repo?.defaultBranch ?? null,
    });

    createSession.mutate({ agentType, sessionId, title, cacheSnapshot });

    // Navigate immediately after kicking off the create so the thread route can
    // refetch the real session row without sitting behind a fresh optimistic
    // placeholder for a full stale-time window.
    navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId },
    });
  }, [createSession, navigate, queryClient, repoId, hasProvider]);

  // On mobile, the home page renders MobileHome which already groups threads
  // by repository. Redirect there instead of showing the old per-repo detail
  // page that exposes the slide-in sidebar.
  if (isMobileLayout) {
    return <Navigate to="/" />;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className={cn(
        "mx-auto max-w-2xl animate-[fade-in_0.2s_ease-out]",
        "px-8 py-10",
      )}>
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex size-10 items-center justify-center rounded-xl border border-white/[0.06] bg-zinc-900">
              <RepoIcon
                repositoryId={repo?.id}
                iconUpdatedAt={repo?.iconUpdatedAt ?? null}
                className="size-5"
                fallbackClassName="text-zinc-500"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg font-semibold tracking-tight text-zinc-100">
                {repo?.name ?? "Repository"}
              </h1>
              {repo && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
                  <span className="max-w-full truncate">{repo.displayName ?? repo.repositoryFullName ?? repo.githubFullName}</span>
                  {repo.language && (
                    <span className="flex items-center gap-1">
                      <span
                        className="inline-block size-2 rounded-full"
                        style={{
                          backgroundColor:
                            langColors[repo.language] || "#71717a",
                        }}
                      />
                      {repo.language}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <GitBranch className="size-3" />
                    {repo.defaultBranch}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canShowRepositoryDeployUi({ repository: repo }) && (
              <Tooltip content="Deploy settings">
                <Link
                  to="/$repoId/deploy"
                  params={{ repoId }}
                  search={{ sessionId: undefined }}
                  className="shrink-0 rounded-lg border border-white/[0.06] p-2 text-zinc-400 transition-colors hover:border-emerald-500/20 hover:bg-emerald-500/5 hover:text-emerald-300"
                >
                  <Rocket className="size-4" />
                </Link>
              </Tooltip>
            )}
            <Tooltip content="Repository settings">
              <Link
                to="/$repoId/settings"
                params={{ repoId }}
                search={{ onboarding: false }}
                className="shrink-0 rounded-lg border border-white/[0.06] p-2 text-zinc-400 transition-colors hover:border-white/[0.1] hover:bg-white/[0.04] hover:text-zinc-200"
              >
                <Settings2 className="size-4" />
              </Link>
            </Tooltip>
            <div ref={agentDropdownRef} className="relative">
              <div className="flex items-stretch">
                <button
                  onClick={() => handleNewThread(selectedAgentType)}
                  disabled={!hasProvider}
                  className="inline-flex items-center gap-1.5 rounded-l-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="size-3.5" />
                  New thread
                </button>
                <button
                  onClick={() => setAgentDropdownOpen((o) => !o)}
                  className="inline-flex items-center border-l border-zinc-300 rounded-r-xl bg-zinc-100 px-1.5 text-zinc-500 transition-colors hover:bg-zinc-200 hover:text-zinc-700"
                >
                  <ChevronDown className="size-3.5" />
                </button>
              </div>
              {agentDropdownOpen && (
                <div className="absolute right-0 top-full z-50 mt-1.5 min-w-44 overflow-hidden rounded-xl border border-white/[0.06] bg-zinc-950 py-1 shadow-2xl shadow-black/50">
                  <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-zinc-600">
                    New thread with…
                  </div>
                  {THREAD_AGENT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSelectedAgentType(opt.value);
                        setAgentDropdownOpen(false);
                        handleNewThread(opt.value);
                      }}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-300 transition-colors hover:bg-white/[0.06] hover:text-zinc-100"
                    >
                      <Plus className="size-3.5 text-zinc-500" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* No provider warning */}
        {!hasProvider && (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/8 px-4 py-3 text-sm text-amber-200/90">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-amber-400">
              <path d="M8 1.5L14.5 13H1.5L8 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              <path d="M8 6.5V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              <circle cx="8" cy="11" r="0.75" fill="currentColor" />
            </svg>
            <span>
              Add an API key or connect your ChatGPT subscription in{" "}
              <Link to="/settings" search={{ tab: "api-keys" as any }} className="underline underline-offset-2 text-amber-100 hover:text-white transition-colors">
                Settings
              </Link>
              {" "}to start a thread.
            </span>
          </div>
        )}

        {/* Sessions list */}
        {sessionsLoading || archivedLoading ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            Loading threads...
          </div>
        ) : (
          (() => {
            const activeThreads = sessions ?? [];
            const archivedThreads = archivedSessions ?? [];
            const normalizedSearch = archivedSearchQuery.trim().toLowerCase();
            const filteredArchivedThreads = normalizedSearch
              ? archivedThreads.filter((s) =>
                  (s.title ?? "Untitled").toLowerCase().includes(normalizedSearch),
                )
              : archivedThreads;

            if (activeThreads.length === 0 && archivedThreads.length === 0) {
              return (
                <div className="rounded-2xl border border-dashed border-white/[0.06] px-6 py-16 text-center">
                  <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl bg-zinc-900 border border-white/[0.06]">
                    <MessageSquare className="size-5 text-zinc-600" />
                  </div>
                  <p className="text-sm text-zinc-400 mb-1">No threads yet.</p>
                  <p className="text-xs text-zinc-600">
                    Start a new thread to code with your AI agent.
                  </p>
                </div>
              );
            }

            return (
              <div className="space-y-6">
                <div>
                  <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-500">
                    Threads
                    <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 tabular-nums text-zinc-500">
                      {activeThreads.length}
                    </span>
                  </div>

                  {activeThreads.length === 0 ? (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3 text-xs text-zinc-500">
                      No active threads.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {activeThreads.map((s) => {
                        const deleting = isDeleting(s);
                        const deleteFailed = isDeleteFailed(s);
                        return (
                          <RepositoryThreadRuntimeState key={s.id} session={s}>
                            {(threadStatus) => {
                              const waitingForReview = threadStatus.needsAttention;
                              return (
                                <div className="group relative">
                                  <Link
                                    to="/$repoId/$sessionId"
                                    params={{ repoId, sessionId: s.id }}
                                    disabled={deleting}
                                    className={cn(
                                      "flex items-center gap-3 rounded-xl border px-4 py-3 transition-all",
                                      deleting
                                        ? "pointer-events-none opacity-40"
                                        : waitingForReview
                                        ? "border-sky-500/30 bg-sky-500/8 hover:border-sky-400/35 hover:bg-sky-500/12"
                                        : "hover:border-white/[0.08] hover:bg-white/[0.04]",
                                      !deleting && !waitingForReview && "border-white/[0.06] bg-white/[0.02]",
                                    )}
                                  >
                                    <ThreadStatusBadge status={threadStatus} />
                                    <div className="min-w-0 flex-1">
                                      <div className={cn(
                                        "truncate text-sm font-medium",
                                        deleting
                                          ? "text-zinc-500"
                                          : waitingForReview
                                          ? "text-zinc-100"
                                          : "text-zinc-200 group-hover:text-zinc-100",
                                      )}>
                                        {s.title ?? "Untitled"}
                                      </div>
                                      <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-600">
                                        {deleteFailed ? (
                                          <span className="flex items-center gap-1 text-red-500">
                                            <AlertTriangle className="size-2.5" />
                                            Delete failed
                                          </span>
                                        ) : null}
                                        <span>{relativeTime(s.createdAt)}</span>
                                        {(s.messageCount ?? 0) > 0 ? <span>{s.messageCount} msgs</span> : null}
                                      </div>
                                    </div>
                                    {!deleting ? <ChevronRight className="size-4 shrink-0 text-zinc-700 transition-colors group-hover:text-zinc-500" /> : null}
                                  </Link>
                                  {deleteFailed ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        deleteSession.mutate(s.id);
                                      }}
                                      disabled={deletingIds.has(s.id)}
                                      className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 rounded-md bg-red-950/40 px-2 py-1 text-xs font-medium text-red-400 transition-colors hover:bg-red-950/70 disabled:opacity-50"
                                    >
                                      Retry
                                    </button>
                                  ) : !deleting ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        void (async () => {
                                          const hasSessionChanges = await resolveArchiveConfirmationHasSessionChanges(s.id, s.prState);

                                          if (
                                            isRunningSandboxForArchiveConfirmation({
                                              persistedSandboxStatus: s.status,
                                              lifecycleStatus: s.lifecycleStatus,
                                              prState: s.prState,
                                              hasSessionChanges,
                                            })
                                            && !window.confirm(RUNNING_SANDBOX_ARCHIVE_CONFIRMATION_MESSAGE)
                                          ) {
                                            return;
                                          }

                                          try {
                                            await archiveSession.mutateAsync(s.id);
                                            captureAnalyticsEvent({
                                              event: ANALYTICS_EVENTS.threadArchived,
                                              properties: buildPrAnalyticsProperties({
                                                sessionId: s.id,
                                                repositoryId: repoId,
                                                agentType: s.agentType,
                                                source: "repo_detail_list",
                                                prState: s.prState,
                                                properties: {
                                                  had_session_changes: hasSessionChanges,
                                                  lifecycle_status: s.lifecycleStatus,
                                                },
                                              }),
                                            });
                                          } catch {
                                            // Mutation state handles rollback and disabled-state cleanup.
                                          }
                                        })();
                                      }}
                                      disabled={archiveMutatingIds.has(s.id)}
                                      className="absolute right-12 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-zinc-500 opacity-0 transition-all hover:bg-white/[0.06] hover:text-zinc-200 group-hover:opacity-100 disabled:opacity-50"
                                      title="Archive thread"
                                    >
                                      <Archive className="size-3.5" />
                                    </button>
                                  ) : null}
                                </div>
                              );
                            }}
                          </RepositoryThreadRuntimeState>
                        );
                      })}
                    </div>
                  )}
                </div>

                {archivedThreads.length > 0 && (
                  <div>
                    <button
                      onClick={() => setArchivedExpanded((prev) => !prev)}
                      className="mb-3 flex w-full items-center gap-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500 transition-colors hover:text-zinc-300"
                    >
                      <ChevronRight className={cn("size-3.5 transition-transform", archivedExpanded && "rotate-90")} />
                      Archived
                      <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 tabular-nums text-zinc-500">
                        {archivedThreads.length}
                      </span>
                    </button>

                    {archivedExpanded && (
                      <div className="animate-[fade-in_0.12s_ease-out]">
                        {/* Search input - show when there are enough threads to warrant filtering */}
                        {archivedThreads.length > ARCHIVED_SEARCH_VISIBILITY_THRESHOLD && (
                          <div className="relative mb-3">
                            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-zinc-600" />
                            <input
                              type="text"
                              value={archivedSearchQuery}
                              onChange={(e) => setArchivedSearchQuery(e.target.value)}
                              placeholder="Search archived threads…"
                              className="w-full rounded-lg border border-white/[0.06] bg-white/[0.03] py-2 pl-9 pr-8 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-white/[0.12] focus:outline-none focus:ring-1 focus:ring-white/[0.06]"
                            />
                            {archivedSearchQuery && (
                              <button
                                onClick={() => setArchivedSearchQuery("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-600 hover:text-zinc-300"
                              >
                                <X className="size-3.5" />
                              </button>
                            )}
                          </div>
                        )}

                        {/* Filtered results info */}
                        {archivedSearchQuery && (
                          <div className="mb-2 text-xs text-zinc-600">
                            {filteredArchivedThreads.length === 0
                              ? "No matching threads"
                              : `${filteredArchivedThreads.length} of ${archivedThreads.length} threads`}
                          </div>
                        )}

                        <div className="space-y-1.5">
                          {filteredArchivedThreads.map((s) => {
                            const deleting = isDeleting(s);
                            const archivedAt = s.archivedAt ?? s.createdAt;
                            return (
                              <RepositoryThreadRuntimeState key={s.id} session={s}>
                                {(threadStatus) => (
                                  <div className="group relative">
                                    <Link
                                      to="/$repoId/$sessionId"
                                      params={{ repoId, sessionId: s.id }}
                                      disabled={deleting}
                                      className={cn(
                                        "flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3 transition-all",
                                        deleting
                                          ? "pointer-events-none opacity-40"
                                          : "text-zinc-400 hover:border-white/[0.08] hover:bg-white/[0.04] hover:text-zinc-200",
                                      )}
                                    >
                                      <ThreadStatusBadge status={threadStatus} />
                                      <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-medium text-zinc-300 group-hover:text-zinc-100">
                                          {s.title ?? "Untitled"}
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-600">
                                          <span>Archived {relativeTime(archivedAt)}</span>
                                          {(s.messageCount ?? 0) > 0 ? <span>{s.messageCount} msgs</span> : null}
                                        </div>
                                      </div>
                                      <ChevronRight className="size-4 shrink-0 text-zinc-700 transition-colors group-hover:text-zinc-500" />
                                    </Link>
                                    {!deleting ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          void (async () => {
                                            try {
                                              await unarchiveSession.mutateAsync(s.id);
                                              captureAnalyticsEvent({
                                                event: ANALYTICS_EVENTS.threadUnarchived,
                                                properties: buildPrAnalyticsProperties({
                                                  sessionId: s.id,
                                                  repositoryId: repoId,
                                                  agentType: s.agentType,
                                                  source: "repo_detail_list",
                                                  prState: s.prState,
                                                }),
                                              });
                                            } catch {
                                              // Mutation state handles rollback and disabled-state cleanup.
                                            }
                                          })();
                                        }}
                                        disabled={archiveMutatingIds.has(s.id)}
                                        className="absolute right-12 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-zinc-500 opacity-0 transition-all hover:bg-white/[0.06] hover:text-zinc-200 group-hover:opacity-100 disabled:opacity-50"
                                        title="Unarchive thread"
                                      >
                                        <ArchiveRestore className="size-3.5" />
                                      </button>
                                    ) : null}
                                  </div>
                                )}
                              </RepositoryThreadRuntimeState>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}

function RepositoryThreadRuntimeState({
  session,
  children,
}: {
  session: Pick<AgentSession, "id" | "status" | "lifecycleStatus" | "prState" | "createdAt" | "updatedAt">;
  children: (threadStatus: ReturnType<typeof deriveSidebarThreadStatusState>) => React.ReactNode;
}) {
  useThreadRuntimeSessionRowMirror(session)
  const runtimeEntry = useThreadRuntime((state) => state.entries[session.id]);
  const threadStatus = deriveSidebarThreadStatusState({
    entry: runtimeEntry,
    persistedStatus: session.status,
    lifecycleStatus: session.lifecycleStatus,
    prState: session.prState,
  });

  return children(threadStatus);
}
